import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import { getAuth } from "firebase/auth";

import config from "@/config/client";
import {
  isAccountDeactivatedError,
  showAccountDeactivatedNotice,
} from "@/lib/account-deactivated";
import { getDeviceLink } from "@/lib/device/device-link";

export type RequestConfig<TVariables = unknown> = {
  method:
    | "get"
    | "put"
    | "patch"
    | "post"
    | "delete"
    | "GET"
    | "PUT"
    | "PATCH"
    | "POST"
    | "DELETE";
  responseType?:
    | "arraybuffer"
    | "blob"
    | "document"
    | "json"
    | "text"
    | "stream";
  headers?: AxiosRequestConfig["headers"];
  signal?: AbortSignal;
  data?: TVariables;
  params?: unknown;
  url: string;
};

const apiUrl = config.apiUrl;

const axiosInstance = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  // Serialize array query params as repeated keys (?status=A&status=B) rather
  // than axios's default bracket form (?status[]=A). NestJS `@Query('status')`
  // only populates from the repeated form; the bracket form is silently dropped,
  // which would apply no filter at all (e.g. completed tasks leaking into the
  // compliance Open tab).
  paramsSerializer: { indexes: null },
});

export const axiosClient = async <
  TData,
  TError = unknown,
  TVariables = unknown,
>(
  config: RequestConfig<TVariables>,
): Promise<AxiosResponse<TData, TError>> => {
  const promise = axiosInstance
    .request<TData>({ ...config })
    .then((res) => res)
    .catch((e: AxiosError<TError>) => {
      throw e;
    });

  return promise;
};

/**
 * The endpoints the offline sync queue replays into — the writes that may
 * carry a client-asserted author (NAU-399). Kept in step with the queue types
 * in lib/offline/syncService.ts.
 */
const DELEGATED_REPLAY_ROUTES: readonly RegExp[] = [
  /^\/v1\/nurseries\/[^/]+\/log-entries$/,
  /^\/v1\/nurseries\/[^/]+\/emergency-light-checks$/,
  /^\/v1\/nurseries\/[^/]+\/event-records$/,
  /^\/v1\/nurseries\/[^/]+\/hot-holding-meals$/,
  /^\/v1\/hot-holding-meals\/[^/]+\/readings$/,
  /^\/v1\/out-of-range-incidents\/[^/]+\/sign-off$/,
  /^\/v1\/hot-holding-alerts\/[^/]+\/acknowledge$/,
  /^\/v1\/compliance-tasks\/instances\/[^/]+\/complete$/,
  /^\/v1\/follow-up-actions\/[^/]+\/status$/,
  /^\/v1\/scheduled-questionnaires\/[^/]+\/answers$/,
  /^\/v1\/scheduled-questionnaires\/[^/]+\/complete$/,
];

/** True for a write the sync queue replays, which may name a different author. */
export function isDelegatedReplayRequest(method: string, url: string): boolean {
  const verb = method.toLowerCase();
  return (
    (verb === "post" || verb === "patch") &&
    DELEGATED_REPLAY_ROUTES.some((route) => route.test(url))
  );
}

// On a linked shared device, stamp replay writes with the device credential.
// The server requires it only when the payload names a different author (a
// queued item captured by whoever held the device before this session); on
// every other request the header is simply ignored. Generated orval functions
// accept no per-call headers, hence an interceptor rather than call-site
// plumbing.
axiosInstance.interceptors.request.use(
  async (requestConfig) => {
    if (
      isDelegatedReplayRequest(
        requestConfig.method ?? "get",
        requestConfig.url ?? "",
      )
    ) {
      try {
        const link = await getDeviceLink();
        if (link?.deviceSecret) {
          requestConfig.headers["x-device-secret"] = link.deviceSecret;
        }
      } catch {
        // IndexedDB unavailable (private mode, teardown) — send without the
        // credential; a delegated payload will be parked by the sync queue.
      }
    }
    return requestConfig;
  },
  (error) => Promise.reject(error),
);

// Add a request interceptor to automatically attach Firebase auth token
axiosInstance.interceptors.request.use(
  async (config) => {
    const auth = getAuth();

    // Wait for Firebase to finish restoring any persisted session before
    // reading currentUser. During app bootstrap a cached session can make the
    // app consider itself authenticated and fire requests (e.g. the nursery
    // list) before `onAuthStateChanged` has populated `currentUser` — those
    // would go out with no Authorization header and 401. authStateReady()
    // resolves as soon as the initial state is known and is a no-op thereafter.
    if (typeof auth.authStateReady === "function") {
      await auth.authStateReady();
    }

    const user = auth.currentUser;

    if (user) {
      // Firebase SDK handles token refresh automatically
      const idToken = await user.getIdToken();
      config.headers["Authorization"] = `Bearer ${idToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// A deactivated account's token keeps working with Firebase until it expires,
// so the API is the first to refuse it. Explain why, then sign out once.
// lib/auth is imported lazily because loading it starts Firebase and the
// offline database, and every API caller (tests included) imports this file.
let signingOutDeactivated = false;
axiosInstance.interceptors.response.use(undefined, (error) => {
  // A request still in flight when the sign-out lands must not show the notice
  // to whoever signs in next.
  if (
    isAccountDeactivatedError(error) &&
    !signingOutDeactivated &&
    getAuth().currentUser
  ) {
    signingOutDeactivated = true;
    showAccountDeactivatedNotice();
    void import("@/lib/auth")
      .then(({ signOut }) => signOut())
      .finally(() => {
        signingOutDeactivated = false;
      });
  }
  return Promise.reject(error);
});

export default axiosClient;
